/**
 * Materials out of the catalog.
 *
 * The catalog does not tell a material from any other model: it is a model tagged
 * `Material*`, and the app's own sheet pulls each category by its tag. What makes it a finish
 * is its `definition`, an XML document whose `<Item For="sticker" Source="[custom]<file>"/>`
 * names the picture among everybody's uploads on files.icandesignapp.com; `Width`/`Height`
 * (centimetres) say how big one repeat of it is in the room.
 *
 * People's own materials carry all of that today. The app's built-in ones are being published
 * with a picture and a full definition (recipes, see docs/client-export-materials-and-models.md);
 * one still without is listed, dimmed, and says why it cannot be applied.
 */
import sections from './materialSections.json';
import { searchModels, thumbnailOf, mongoUnescape, type CatalogModel } from './catalogApi';
import { attr, kids, parseXml, type Node } from '../core/hp/xml';
import type { RGB, Surface } from '../core/hp/types';
import { hasKey, t, translate } from '../i18n/I18n';

/** The shape of the staged library the sheet once read; project finishes are still matched against it. */
export interface MaterialEntry {
  surface: 'floor' | 'wall' | 'item';
  collection: string;
  caption: string;
  texture: string;
  url: string;
  thumb?: string;
  /** Tile size in metres — the reason a material cannot be just an image. */
  tile: [number, number];
  roughness: number;
}

export interface MaterialSection { id: string; title: string; tags: string[] }
/** The sections, titled in the interface language (src/i18n/langs/catalog, tools/build-catalog-langs.ts). */
export const MATERIAL_SECTIONS: MaterialSection[] = sections.map(section => ({ ...section, get title() {
  const key = '@material/' + section.id;
  return hasKey(key) ? translate(key) : section.title;
} }));

/** One tile of the sheet: a catalog model that stands for a finish. */
export interface CatalogMaterial {
  id: string;
  caption: string;
  thumb?: string;
  /** One repeat in metres, from the model's catalog size. */
  tile: [number, number];
  definitionUrl?: string;
  /** The app's own texture path (`wall/bricks/x.jpg`) when this is a built-in. */
  builtinTexture?: string;
  /** Published by a person, hence with a picture of its own. */
  own: boolean;
}

export const MATERIAL_PAGE = 40;
const BUILTIN_TEXTURES = 'Assets/Resources/Textures/';

export function catalogMaterialOf(model: CatalogModel): CatalogMaterial {
  const builtin = model.builtinId ? mongoUnescape(model.builtinId) : undefined;
  const width = positive(model.width) ?? 100, height = positive(model.height) ?? width;
  const texture = builtin?.startsWith(BUILTIN_TEXTURES) ? builtin.slice(BUILTIN_TEXTURES.length) : undefined;
  // A built-in was published without a title; its file name is the only name it has.
  const fileName = texture?.split('/').pop()?.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
  return {
    id: model.id,
    caption: model.title?.trim() || fileName || '',
    thumb: thumbnailOf(model),
    tile: [metres(width), metres(height)],
    definitionUrl: typeof model.definition === 'string' && /^https?:\/\//.test(model.definition) ? model.definition : undefined,
    builtinTexture: texture,
    own: !!model.accountId,
  };
}

/** Centimetres to metres, to the millimetre and never thinner than 5 cm. */
function metres(cm: number): number { return Math.round(Math.max(cm, 5) * 10) / 1000; }

function positive(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

/**
 * Where a section's listing stands. People's materials come first, because every one of
 * them can be applied; the catalog's whole answer follows, less what was already shown.
 *
 * Temporary: once the built-ins are published with pictures (stage 3б of
 * docs/migration-to-own-server.md) the two phases go, and a section is listed in the app's order.
 */
/**
 * The order of the app's sheet: its own built-ins first, then what people published. (The app
 * puts the person's own materials ahead of both; the web has no account yet.)
 */
export interface MaterialCursor { phase: 'builtin' | 'people'; skip: number; done: boolean }
export const firstMaterialCursor = (): MaterialCursor => ({ phase: 'builtin', skip: 0, done: false });

export async function loadMaterialPage(section: MaterialSection, cursor: MaterialCursor): Promise<{ items: CatalogMaterial[]; cursor: MaterialCursor }> {
  const items: CatalogMaterial[] = [];
  let { phase, skip, done } = cursor;
  // A page may hold nothing new (see below); keep going rather than show an empty step.
  for (let calls = 0; !done && !items.length && calls < 6; calls++) {
    const builtin = phase === 'builtin';
    const models = await searchModels({ anyTags: section.tags, skip, pagesize: MATERIAL_PAGE, builtinOnly: builtin, publishedByPeople: !builtin });
    skip += models.length;
    // A catalog that does not know `onlyWithoutAccount` yet answers with everything; people's
    // materials are then left to their own phase.
    for (const model of models) if (!builtin || !model.accountId) items.push(catalogMaterialOf(model));
    if (models.length < MATERIAL_PAGE) {
      if (builtin) { phase = 'people'; skip = 0; } else done = true;
    }
  }
  return { items, cursor: { phase, skip, done } };
}

/** Why a material cannot be applied, in words for the person looking at it. */
export class MaterialNotPrepared extends Error {
  constructor() { super(t('#MaterialsNotPrepared')); }
}

const surfaces = new Map<string, Promise<Surface>>();

/** The finish a catalog material stands for. Rejects with `MaterialNotPrepared` when it has no picture. */
export function materialSurface(material: CatalogMaterial): Promise<Surface> {
  const known = surfaces.get(material.id);
  if (known) return known;
  const request = readSurface(material).catch(error => {
    // Only an answer is worth keeping; a failed request may succeed next time.
    if (!(error instanceof MaterialNotPrepared) && surfaces.get(material.id) === request) surfaces.delete(material.id);
    throw error;
  });
  surfaces.set(material.id, request);
  return request;
}

/** The finish of a material whose definition was already read, for marking the current one. */
const settled = new Map<string, Surface>();
export function knownMaterialSurface(id: string): Surface | undefined { return settled.get(id); }

async function readSurface(material: CatalogMaterial): Promise<Surface> {
  if (!material.definitionUrl) throw new MaterialNotPrepared();
  const response = await fetch(material.definitionUrl);
  if (!response.ok) throw new Error(t('#MaterialsDefinitionHttpError', response.status));
  const surface = definitionSurface(parseDefinition(await response.text()), { tile: material.tile, caption: material.caption });
  if (!surface) throw new MaterialNotPrepared();
  settled.set(material.id, surface);
  return surface;
}

export interface MaterialDefinition {
  /** The bare name of the uploaded picture. */
  source?: string;
  tile?: [number, number];
  caption?: string;
  /** The app's tint (`Color="#AC9B7E"`), the whole finish when there is no picture. */
  color?: RGB;
  /** The app's surface type: `mtl_wood`, `mtl_polished`, … */
  type?: string;
  /** 0..1; the app writes recipes in percent. */
  shininess?: number;
}

/**
 * What a model's definition says about its finish. People's materials carry the sticker
 * inside `<Variations>`; the app's recipes may carry it on the root item itself.
 */
export function parseDefinition(xml: string): MaterialDefinition {
  let root: Node | undefined;
  try { root = parseXml(xml).Item; } catch { return {}; }
  if (!root) return {};
  const sticker = findSticker(root) ?? root;
  const raw = attr(sticker, 'Source');
  const source = raw?.startsWith('[custom]') ? raw.slice('[custom]'.length).trim() : undefined;
  const width = positive(Number(attr(root, 'Width'))), height = positive(Number(attr(root, 'Height')));
  const shininess = Number(attr(sticker, 'Shininess') ?? attr(root, 'Shininess'));
  const definition: MaterialDefinition = {
    source: source && !/[\\/]|\.\./.test(source) ? source : undefined,
    tile: width ? [metres(width), metres(height ?? width)] : undefined,
    caption: attr(root, 'Caption')?.trim(),
  };
  const color = rgbOf(attr(sticker, 'Color') ?? attr(root, 'Color'));
  if (color) definition.color = color;
  const type = (attr(sticker, 'Material') ?? attr(root, 'Material'))?.trim();
  if (type) definition.type = type;
  if (Number.isFinite(shininess) && shininess >= 0) definition.shininess = Math.min(shininess > 1 ? shininess / 100 : shininess, 1);
  return definition;
}

/** The finish a definition stands for, or nothing when it has neither a picture nor a tint. */
export function definitionSurface(definition: MaterialDefinition, fallback: { tile?: [number, number]; caption?: string } = {}): Surface | undefined {
  if (!definition.source && !definition.color) return undefined;
  const surface: Surface = {};
  if (definition.source) {
    surface.textureKind = 'custom';
    surface.customTexture = definition.source;
    const tile = definition.tile ?? fallback.tile;
    if (tile) surface.tileMeters = tile;
  }
  if (definition.color) surface.color = definition.color;
  if (definition.type) surface.type = definition.type;
  if (definition.shininess !== undefined) surface.shininess = definition.shininess;
  const caption = definition.caption || fallback.caption;
  if (caption) surface.caption = caption;
  return surface;
}

function rgbOf(hex: string | undefined): RGB | undefined {
  const digits = hex?.trim().replace(/^#/, '');
  if (!digits || !/^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(digits)) return undefined;
  return [0, 2, 4].map(i => parseInt(digits.slice(i, i + 2), 16) / 255) as RGB;
}

function findSticker(node: Node, depth = 0): Node | undefined {
  if (depth > 4) return undefined;
  for (const tag of ['Item', 'Variations']) for (const child of kids(node, tag)) {
    if (tag === 'Item' && attr(child, 'For') === 'sticker' && (attr(child, 'Source') || attr(child, 'Color'))) return child;
    const deeper = findSticker(child, depth + 1);
    if (deeper) return deeper;
  }
  return undefined;
}

/** Forgets every answer — for tests. */
export function resetMaterials(): void { surfaces.clear(); settled.clear(); }
